<script>
	// NPM Modules
	import { flip } from 'svelte/animate';
	import AutoComplete from 'simple-svelte-autocomplete';
	// Stores
	import SportStore from '@/Stores/Sport/SportStore.js';
	import CompetitionStore from '@/Stores/Sport/CompetitionStore.js';
	import MultiExpressGroupStore from '@/Stores/MultiExpressGroupStore.js';
	import ParentMarketStore from '@/Stores/Sport/ParentMarketStore.js';
	// Constants
	// Components
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import Prompt from '@/Components/Prompt.svelte';
	import Icon from '@/Components/Icon.svelte';
	import PillMultiSelect from '@/Components/PillMultiSelect.svelte';
	// Partials
	import MultiExpressGroupMarkets from '@/Partials/MultiExpress/_MultiExpressGroupMarkets.svelte';
	// Services
	import { notify } from '@/Services/Notify.js';

	export let multiExpressGroup;
	export let first;
	export let last;

	let sending = false;
	let deleting = false;
	let promptDelete = false;
	let competitions;
	let selectedParentMarket = null;

	// Get all sports with comps
	SportStore.getSports(true);

	$: selectedSportId = multiExpressGroup && multiExpressGroup.sport_id;
	$: selectedCompetitionId = multiExpressGroup && multiExpressGroup.competition_id;
	$: selectedSportId && getCompetitionsForSport(selectedSportId);
	$: sportOptions =
		$SportStore &&
		$SportStore
			.sortBy('name')
			.map((sport) => {
				return [sport.id, sport.name];
			})
			.all();

	function getCompetitionsForSport(sportId) {
		if (sportId) {
			CompetitionStore.getCompetitionsForSport(sportId).then(() => {
				competitions =
					$CompetitionStore
						.where('visible', true)
						.sortBy('name')
						.map((comp) => {
							return [comp.id, comp.name];
						})
						.all() ?? false;
			});
		}
	}

	function onSave() {
		sending = true;
		MultiExpressGroupStore.updateMultiExpressGroup(multiExpressGroup)
			.then(() => {
				notify({
					type: 'success',
					title: 'Multi Express Group',
					message: 'Multi Express Group updated',
					timeout: 3
				});
			})
			.catch(() => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: 'Failed to update Multi Express Group',
					timeout: 5
				});
			})
			.finally(() => (sending = false));
	}

	function onDelete() {
		deleting = true;
		MultiExpressGroupStore.deleteMultiExpressGroup(multiExpressGroup)
			.then(() => {
				notify({
					type: 'success',
					title: 'Multi Express Group',
					message: 'Multi Express Group Deleted',
					timeout: 3
				});
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to delete the Multi Express Group',
					timeout: 5
				});
			})
			.finally(() => (deleting = false));
	}

	function shiftOrderUp() {
		MultiExpressGroupStore.changeMultiExpressGroupOrder(multiExpressGroup.order, multiExpressGroup.order - 1);
	}

	function shiftOrderDown() {
		MultiExpressGroupStore.changeMultiExpressGroupOrder(multiExpressGroup.order, multiExpressGroup.order + 1);
	}

	function changeSport(sportId) {
		if (typeof parseInt(sportId) !== 'undefined' && sportId !== false) {
			multiExpressGroup.sport_id = sportId;
		}
	}

	function changeCompetition(competition_id) {
		multiExpressGroup.competition_id = competition_id;
	}

	async function searchParentMarket(keyword) {
		return ParentMarketStore.searchParentMarketsForCompetition(keyword, selectedCompetitionId).then(() => {
			return $ParentMarketStore.sortBy('name').sortBy('order').all();
		});
	}

	//For multi express group parent markets
	$: firstOrderId =
		multiExpressGroup && multiExpressGroup.parent_markets && multiExpressGroup.parent_markets.data.min('order');
	$: lastOrderId =
		multiExpressGroup && multiExpressGroup.parent_markets && multiExpressGroup.parent_markets.data.max('order');

	function onAddMultiExpressGroupParentMarket(selectedParentMarket, multiExpressGroupId) {
		if (selectedParentMarket) {
			let parentMarket = $ParentMarketStore.firstWhere('id', String(selectedParentMarket));
			if (parentMarket) {
				MultiExpressGroupStore.addNewMultiExpressGroupParentMarketToStore(multiExpressGroup.id, parentMarket);
			}
		}

		// Get the input element by its ID
		const inputElement = document.getElementById('parent-market-search-' + multiExpressGroupId);
		if (inputElement) {
			// Clear the input field's value
			inputElement.value = '';
		}

		selectedParentMarket = null;
	}
</script>

<Prompt
	bind:open={promptDelete}
	title="Confirm delete"
	message="This can't be undone. Are you sure?"
	onConfirm={() => onDelete()}
/>

<div class="pt-2 px-2 bg-white border">
	<div class="flex flex-row">
		<div class="flex-1">
			<div class="flex space-x-2">
				<div>
					<div class="flex flex-col items-center justify-center">
						<button
							on:click={() => shiftOrderUp()}
							disabled={multiExpressGroup.order === first}
							class:opacity-0={multiExpressGroup.order === first}
						>
							<Icon name="up-arrow" classes="w-7 h-7" />
						</button>
						<span class="my-px">{multiExpressGroup.order}</span>
						<button
							on:click={() => shiftOrderDown()}
							disabled={multiExpressGroup.order === last}
							class:opacity-0={multiExpressGroup.order === last}
						>
							<Icon name="down-arrow" classes="w-7 h-7" />
						</button>
					</div>
				</div>
				<div class="flex flex-row pt-1 border-gray-300 mt-2">
					<PillMultiSelect
						className="w-40"
						name="sport"
						label="Sport"
						singleSelection={true}
						options={[...sportOptions]}
						action=""
						checked={[multiExpressGroup.sport_id]}
						disabled={!multiExpressGroup.temp_id}
						on:change={(change) => changeSport(change.detail.checked[0])}
					/>

					<PillMultiSelect
						className="min-w-24"
						name="competition"
						label="Competition"
						singleSelection={true}
						options={competitions ? competitions : []}
						action=""
						checked={[multiExpressGroup.competition_id]}
						disabled={!multiExpressGroup.temp_id}
						on:change={(change) => changeCompetition(change.detail.checked[0])}
					/>
				</div>
			</div>
			<div class="flex space-x-10 ml-20 mb-5">
				<div class="flex flex-row pt-1 border-gray-300 mt-2">
					{#if !multiExpressGroup.temp_id}
						<div class="w-60 mt-1 text-sm">
							<AutoComplete
								bind:value={selectedParentMarket}
								inputId={`parent-market-search-${multiExpressGroup.id}`}
								className="w-200 border-gray-300 bg-gray-500 rounded-full"
								inputClassName="w-full text-sm text-white bg-gray-500 rounded-full"
								dropdownClassName="rounded"
								placeholder="Search Parent Market..."
								searchFunction={searchParentMarket}
								hideArrow={true}
								showClear={true}
								localFiltering={false}
								labelFunction={(parentMarket) =>
									parentMarket.name +
									(!parentMarket.market_type.rolling_type
										? parentMarket.line
											? ' (' + parentMarket.line + ')'
											: ''
										: ' (Rolling)')}
								valueFieldName="id"
							/>
						</div>

						<div class="w-1/4 text-center justify-center mt-1">
							<LoadingButton
								on:click={() => onAddMultiExpressGroupParentMarket(selectedParentMarket, multiExpressGroup.id)}
								className="btn btn-primary"
								loading={sending}
							>
								{'Add'}
							</LoadingButton>
						</div>
					{/if}
				</div>
			</div>
		</div>
		<div class="flex flex-col flex-shrink justify-center space-y-2">
			<LoadingButton on:click={onSave} className="btn btn-primary" loading={sending}>
				{multiExpressGroup.temp_id ? 'Create' : 'Save'}
			</LoadingButton>
			<LoadingButton
				on:click={() => (multiExpressGroup.new ? onDelete() : (promptDelete = true))}
				className="btn btn-primary-action"
				loading={deleting}
				>Delete
			</LoadingButton>
		</div>
	</div>

	<div class="flex flex-row">
		<div class="flex-1">
			<!-- /Page Header -->
			{#if multiExpressGroup.parent_markets}
				<div class="p-2 mx-auto max-w-custom">
					<div class="mt-2 rounded">
						<div class="space-y-2">
							<div>
								<div class="relative">
									<div class="space-y-2 list">
										{#each multiExpressGroup.parent_markets.data
											.sortBy('order')
											.all() as parentMarket (parentMarket.id ?? parentMarket.temp_id)}
											<div class="item" data-order={parentMarket.order} animate:flip={{ duration: 200 }}>
												<svelte:component
													this={MultiExpressGroupMarkets}
													{parentMarket}
													first={firstOrderId}
													last={lastOrderId}
												/>
											</div>
										{/each}
									</div>
								</div>
							</div>
							<div class="py-2 mb-4 border-b" />
						</div>
					</div>
				</div>
			{/if}
		</div>
	</div>
</div>
